import { LitElement, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { enumAttributeConverter } from '../enum-types';
import { getGlobalStyleSheets } from '../../global-styles';

import { AtlIconProps, AtlAriaIconProps } from './icon.props';
import * as Enums from './icon.props';
import { ifDefined } from 'lit/directives/if-defined.js';
import { styleMap } from 'lit/directives/style-map.js';

import * as TagNames from './icon.tagnames';
import { IconVariant } from './icon.enums';

const IconVariantValuesMap = {
    [IconVariant.Primary]: "primary",    
    [IconVariant.Secondary]: "secondary",
    [IconVariant.Success]: "success",
    [IconVariant.Warning]: "warning",
    [IconVariant.Danger]: "danger",
    [IconVariant.PrimaryInverse]: "primary-inverse",    
    [IconVariant.SecondaryInverse]: "secondary-inverse",
    [IconVariant.SuccessInverse]: "success-inverse",
    [IconVariant.WarningInverse]: "warning-inverse",
    [IconVariant.DangerInverse]: "danger-inverse",
    [IconVariant.GreyScale]: "greyscale",
    [IconVariant.GreyScaleInverse]: "greyscale-inverse",
};

const IconSizesValues = {
   [Enums.IconSizes.VeryTiny]: '0.4',
   [Enums.IconSizes.ExtraSmaller]: '0.675',
   [Enums.IconSizes.ExtraSmall]: '0.75',
   [Enums.IconSizes.Small]: '0.875',
   [Enums.IconSizes.Medium]: '1',
   [Enums.IconSizes.Large]: '1.25',
   [Enums.IconSizes.XLarge]: '2',
   [Enums.IconSizes.Zoom1x]: '1',
   [Enums.IconSizes.Zoom2x]: '2',
   [Enums.IconSizes.Zoom3x]: '3',
   [Enums.IconSizes.Zoom4x]: '4',
   [Enums.IconSizes.Zoom5x]: '5',
   [Enums.IconSizes.Zoom6x]: '6',
   [Enums.IconSizes.Zoom7x]: '7',
   [Enums.IconSizes.Zoom8x]: '8',
   [Enums.IconSizes.Zoom9x]: '9',
   [Enums.IconSizes.Zoom10x]: '10',
};

@customElement(TagNames.Icon)
export class AtlIcon extends LitElement implements AtlIconProps {
   declare iconId: string;
   declare iconSource: Enums.IconSource;
   declare iconSize: Enums.IconSizes;
   declare iconSet: Enums.IconSet;
   declare iconVariant: Enums.IconVariant;
   declare name: string;
   declare iconClassName: string;
   declare iconAriaSettings?: AtlAriaIconProps;
   declare clickable?: boolean;

   static properties = {
      iconId: { type: String },
      iconSource: {
         type: String,
         converter: enumAttributeConverter(
            Enums.IconSource,
            Enums.IconSource.OpenIconic
         ),
      },
      iconSize: {
         type: String,
         converter: enumAttributeConverter(Enums.IconSizes),
      },
      iconSet: {
         type: String,
         converter: enumAttributeConverter(Enums.IconSet, Enums.IconSet.Solid),
      },
      iconVariant: {
         type: String,
         converter: enumAttributeConverter(Enums.IconVariant, Enums.IconVariant.Primary),
      },
      name: { type: String },
      iconClassName: { type: String },
      iconAriaSettings: { attribute: 'icon-aria-settings', type: Object },
      clickable: { type: Boolean },
   };

   constructor() {
      super();
      this.iconId = '';
      this.name = '';
      this.iconClassName = '';
   }

   static styles = [...getGlobalStyleSheets()];

   render() {
      return AtlIconTemplate({
         iconSource: this.iconSource,
         iconName: this.name,
         iconSet: this.iconSet,
         iconSize: this.iconSize,
         iconVariant: this.iconVariant,
         iconClassNames: this.iconClassName,
         ariaLabel: this.iconAriaSettings?.ariaLabel,
         ariaHidden: this.iconAriaSettings?.ariaHidden,
         ariaExpanded: this.iconAriaSettings?.ariaExpanded,
         ariaHasPopup: this.iconAriaSettings?.ariaHasPopup,
         role: this.iconAriaSettings?.role,
         clickable: this.clickable,
      });
   }
}

export function AtlIconClassFactory(icon: {
   iconSource?: Enums.IconSource;
   iconName?: string;
   iconSet?: Enums.IconSet;
   iconVariant?: Enums.IconVariant;
   clickable?: boolean;
}) {
   const classNames = [] as string[];
   if (!icon.iconSource || icon.iconSource === Enums.IconSource.FontAwesome) {
      const iconSet = icon.iconSet || Enums.IconSet.Solid;
      classNames.push(`fa-${iconSet.toLowerCase()}`);
      if (icon.iconName) {
         classNames.push(`fa-${icon.iconName}`);
      }
   } else if (icon.iconName) {
      classNames.push(`oi oi-${icon.iconName}`);
   }

   classNames.push(`kpmg-icon ${icon.iconVariant && IconVariantValuesMap[icon.iconVariant]}`);
   if (icon.clickable){
      classNames.push("clickable")
   }

   return classNames;
}

export function AtlIconTemplate(icon: {
   iconSource?: Enums.IconSource;
   iconName: string;
   iconSet?: Enums.IconSet;
   iconSize?: Enums.IconSizes;
   iconVariant?: Enums.IconVariant;
   iconClassNames?: string;
   iconStyle?: { [key: string]: string };
   ariaHidden?: boolean;
   ariaLabel?: string;
   ariaExpanded?: boolean;
   ariaHasPopup?: boolean;
   role?: AtlAriaIconProps['role'];
   clickable?: boolean;
}) {
   const classNames = AtlIconClassFactory({
      iconSource: icon.iconSource,
      iconName: icon.iconName,
      iconSet: icon.iconSet,
      iconVariant: icon.iconVariant,
      clickable: icon.clickable
   });
   if (icon.iconClassNames) {
      classNames.push(icon.iconClassNames);
   }

   const iconStyle: { [key: string]: string } = { ...icon.iconStyle };
   if (icon.iconSize) {
      iconStyle['font-size'] = `${IconSizesValues[icon.iconSize]}rem`;
   }

   if (icon.clickable) {
      const isFocusable = icon.ariaHidden !== true;
      return html`
         <i
            part="icon"
            class="${classNames.join(' ')}"
            style="${styleMap(iconStyle)}"
            aria-label=${ifDefined(icon?.ariaLabel)}
            aria-hidden=${ifDefined(icon?.ariaHidden)}
            aria-expanded=${ifDefined(icon?.ariaExpanded)}
            aria-haspopup=${ifDefined(icon?.ariaHasPopup)}
            role=${ifDefined(icon?.role ?? 'button')}
            tabindex=${ifDefined(isFocusable ? 0 : undefined)}
            @keydown=${isFocusable
                ? (e: KeyboardEvent) => {
                     if (e.key === 'Enter' && !e.repeat) {
                        e.preventDefault();
                        (e.currentTarget as HTMLElement).click();
                     }
                     if (e.key === ' ') {
                        // Prevent page scroll; activation happens on keyup to match native button behavior.
                        e.preventDefault();
                     }
                  }
                : undefined}
            @keyup=${isFocusable
                ? (e: KeyboardEvent) => {
                     if (e.key === ' ' && !e.repeat) {
                        e.preventDefault();
                        (e.currentTarget as HTMLElement).click();
                     }
                  }
                : undefined}
         ></i>
      `;
   }

   return html`
      <i
         part="icon"
         class="${classNames.join(' ')}"
         style="${styleMap(iconStyle)}"
         aria-hidden=${ifDefined(icon?.ariaHidden)}
         aria-label=${ifDefined(icon?.ariaLabel)}
         aria-expanded=${ifDefined(icon?.ariaExpanded)}
         aria-haspopup=${ifDefined(icon?.ariaHasPopup)}
         role=${ifDefined(icon?.role)}
      ></i>
   `;
}